/**
 * Direct-to-S3 upload via a presigned PUT URL.
 *
 * Shared by the upload page (first upload) and the dashboard (replacing an
 * existing report's HTML). Uses XMLHttpRequest rather than fetch because
 * fetch still has no upload-progress event, and a 25 MB report on a hotel
 * wifi needs a progress bar to not look hung.
 */
export function putToS3(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.responseText}`));
    });
    xhr.addEventListener("error", () => reject(new Error("Network error during S3 upload")));
    xhr.open("PUT", url);
    // Must match the ContentType the URL was signed with, or S3 rejects the
    // request, and it is what the server-side content-type check reads.
    xhr.setRequestHeader("Content-Type", "text/html");
    xhr.send(file);
  });
}

/**
 * Opens a native file picker and resolves with the chosen file, or null if
 * the user cancels.
 *
 * Note there is no reliable cancel event for a file input across browsers —
 * the `cancel` event is recent and not universal — so a dismissed picker
 * simply never resolves the file. The caller must therefore treat "no file"
 * as "nothing happened" and leave its UI in a usable state, which is why
 * this resolves null on the cancel event where available and is otherwise
 * garbage-collected with the element.
 */
export function pickFile(accept: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.style.display = "none";
    document.body.appendChild(input);

    const done = (file: File | null): void => {
      input.remove();
      resolve(file);
    };

    input.addEventListener("change", () => done(input.files?.[0] ?? null));
    input.addEventListener("cancel", () => done(null));
    input.click();
  });
}
